/**
 * Static Episodes Data for Production Builds
 *
 * This file uses Vite's import.meta.glob() to statically import all markdown
 * episode files at build time. This creates a production bundle with all episodes
 * pre-loaded for optimal performance.
 *
 * For development mode, episodes are loaded dynamically via the episodesLoader service.
 */

import matter from 'gray-matter';
import { marked } from 'marked';
import { Episode } from '../types/Episode';

// Configure marked for consistent rendering
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Import all markdown files from public/posts/
// eager: true loads them synchronously at build time
const episodeFiles = import.meta.glob('/public/posts/*.md', {
  eager: true,
  query: '?raw',
  import: 'default'
});

/**
 * Parse all episode files and return sorted array
 */
export function getStaticEpisodes(): Episode[] {
  const episodes: Episode[] = [];

  for (const [filepath, content] of Object.entries(episodeFiles)) {
    const rawContent = content as string;
    const { data } = matter(rawContent);

    // Only include published episodes
    if (data.status === 'published') {
      // Extract filename from path
      const filename = filepath.split('/').pop() || '';

      episodes.push({
        episode: data.episode || 0,
        title: data.title,
        slug: data.slug,
        date: data.date,
        author: data.author,
        excerpt: data.excerpt,
        readingTime: data.readingTime,
        tags: data.tags || [],
        featured: data.featured || false,
        status: data.status,
        thumbnail: data.thumbnail,
        filename: filename,
        clipboardNote: data.clipboardNote,
        clipboardCategory: data.clipboardCategory,
        // Don't include full content in the list - it will be loaded on demand
      });
    }
  }

  // Sort by episode number (descending: newest first)
  episodes.sort((a, b) => b.episode - a.episode);

  return episodes;
}

/**
 * Get only featured episodes
 */
export function getStaticFeaturedEpisodes(): Episode[] {
  return getStaticEpisodes().filter(ep => ep.featured);
}

/**
 * Get episode by slug with full content
 */
export function getStaticEpisodeBySlug(slug: string): Episode | null {
  // Find the file for this slug
  for (const [filepath, content] of Object.entries(episodeFiles)) {
    const rawContent = content as string;
    const { data, content: markdownContent } = matter(rawContent);

    if (data.slug === slug && data.status === 'published') {
      const filename = filepath.split('/').pop() || '';

      // Convert markdown to HTML
      const htmlContent = marked(markdownContent) as string;

      return {
        episode: data.episode || 0,
        title: data.title,
        slug: data.slug,
        date: data.date,
        author: data.author,
        excerpt: data.excerpt,
        readingTime: data.readingTime,
        tags: data.tags || [],
        featured: data.featured || false,
        status: data.status,
        thumbnail: data.thumbnail,
        filename: filename,
        clipboardNote: data.clipboardNote,
        clipboardCategory: data.clipboardCategory,
        // Include full HTML content for individual episode view
        content: htmlContent,
      };
    }
  }

  return null;
}

/**
 * Get all unique tags from episodes
 */
export function getStaticTags(): string[] {
  const episodes = getStaticEpisodes();
  const tags = new Set<string>();

  episodes.forEach(episode => {
    episode.tags.forEach(tag => tags.add(tag));
  });

  return Array.from(tags).sort();
}

/**
 * Search episodes by query (title or excerpt)
 */
export function searchStaticEpisodes(query: string): Episode[] {
  const episodes = getStaticEpisodes();
  const lowerQuery = query.toLowerCase();

  return episodes.filter(episode =>
    episode.title.toLowerCase().includes(lowerQuery) ||
    episode.excerpt.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Filter episodes by tag
 */
export function getStaticEpisodesByTag(tag: string): Episode[] {
  const episodes = getStaticEpisodes();
  return episodes.filter(episode => episode.tags.includes(tag));
}

/**
 * Get clipboard notes from episodes with clipboard data
 * Transforms episodes into clipboard note format for the Clipboard tool
 */
export interface ClipboardNote {
  id: number;
  content: string;
  category: 'users' | 'pfy' | 'management' | 'technical';
  episodeSlug: string;
  episodeTitle: string;
  episodeNumber: number;
}

export function getClipboardNotes(): ClipboardNote[] {
  const episodes = getStaticEpisodes();

  // Filter episodes that have clipboard notes
  const episodesWithNotes = episodes.filter(ep => ep.clipboardNote && ep.clipboardCategory);

  // Transform to clipboard note format
  return episodesWithNotes.map((episode, index) => ({
    id: index + 1,
    content: episode.clipboardNote!,
    category: episode.clipboardCategory!,
    episodeSlug: episode.slug,
    episodeTitle: episode.title,
    episodeNumber: episode.episode,
  }));
}
